import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import parentApi from "@/lib/parentApi";
import { YearCalendar, CalendarLegend, NamedBreaks } from "@/components/YearCalendar";

export default function ParentCalendarPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const backTo = sp.get("student") ? `/parent/student/${sp.get("student")}` : "/parent";
  const [year, setYear] = useState(new Date().getFullYear());
  const [schools, setSchools] = useState(null);
  const [active, setActive] = useState(0);

  const load = useCallback(async (y) => {
    try {
      const { data } = await parentApi.get(`/parent/calendar?year=${y}`);
      setSchools(data);
      const want = data.findIndex((x) => x.company_id === sp.get("school"));
      setActive((a) => (want >= 0 ? want : Math.min(a, Math.max(0, data.length - 1))));
    } catch {
      nav("/parent/login");
    }
  }, [nav, sp]);

  useEffect(() => {
    if (!localStorage.getItem("vlog_parent_token")) { nav("/parent/login"); return; }
    load(year);
  }, [year, load, nav]);

  const school = schools?.[active];
  const entriesMap = Object.fromEntries((school?.entries || []).map((d) => [d.date, d]));

  return (
    <div className="flex-1 bg-slate-50" data-testid="parent-calendar-page">
      <header className="px-6 pt-2 pb-4">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm px-5 py-4 flex items-center gap-3">
          <button onClick={() => nav(backTo)} data-testid="parent-calendar-back" className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-700 inline-flex items-center justify-center hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="h-9 w-9 rounded-xl bg-rose-500/20 flex items-center justify-center">
            <CalendarDays className="h-4 w-4 text-rose-300" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold leading-tight">School calendar</div>
            <div className="text-[11px] text-slate-500">Holidays, breaks & teacher work days</div>
          </div>
          <div className="flex items-center gap-1" data-testid="parent-calendar-year-nav">
            <button onClick={() => setYear((y) => y - 1)} data-testid="parent-calendar-prev-year" className="h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-700 inline-flex items-center justify-center hover:bg-slate-100">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="font-display font-bold w-12 text-center" data-testid="parent-calendar-year">{year}</span>
            <button onClick={() => setYear((y) => y + 1)} data-testid="parent-calendar-next-year" className="h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-700 inline-flex items-center justify-center hover:bg-slate-100">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="w-full px-6 pb-6 space-y-3">
        {schools === null && (
          <div className="rounded-2xl bg-white p-10 text-center shadow-sm"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        )}
        {schools !== null && schools.length === 0 && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm text-sm text-slate-500" data-testid="parent-calendar-empty">
            No school linked yet — ask your school for an invite.
          </div>
        )}
        {schools !== null && schools.length > 1 && (
          <div className="flex gap-2 flex-wrap" data-testid="parent-calendar-school-tabs">
            {schools.map((s, i) => (
              <button key={s.company_id} onClick={() => setActive(i)} data-testid={`parent-calendar-school-${i}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                  i === active ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"
                }`}>
                {s.school_name}
              </button>
            ))}
          </div>
        )}
        {school && (
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="font-display font-bold text-slate-900 text-sm">{school.school_name}</div>
              <CalendarLegend />
            </div>
            <YearCalendar year={year} entries={entriesMap} />
            <NamedBreaks entries={school.entries} />
            <div className="mt-3 text-xs text-slate-500">{school.entries.length} non-school day{school.entries.length === 1 ? "" : "s"} in {year}</div>
          </div>
        )}
      </main>
    </div>
  );
}
